import {getActiveState} from 'client/framework';
import {useCallback, useEffect, useRef, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {normalizeError} from 'lib/normalizeError';

/** Legacy People roster paged 25/row; matched here for parity (and the v2 Folders/Assignments idiom). */
export const ROSTER_PAGE_SIZE = 25;

/** The slice of the Mandark query builder this hook drives — a query already scoped to one page. */
interface PagedQuery {
  invalidateInterest: () => void;
  getResourcePromise: () => Promise<{toArray: () => unknown[]}>;
  getResourceMetadata: () => {getIn: (path: string[], notSetValue: number) => number};
  isResourceStale: () => boolean;
}

interface Args {
  /**
   * Builds the query for a 1-indexed page (with sort + search already applied). MUST be stable per
   * (classId, search, sort) — wrap in useCallback keyed on those primitives — because a new identity
   * resets pagination to page one and refetches (that is how search/sort changes reset the list).
   */
  buildQuery: (page: number) => PagedQuery;
  /** Pluralized noun for the "Couldn't load more <label>" toast (e.g. "students"). */
  label: string;
}

interface Result<Row> {
  rows: Row[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  /** A cold-load (page one) failure — show a retry panel rather than a false empty state. */
  hasError: boolean;
  loadMore: () => void;
  /** Re-pull page one, resetting to the top. Also the retry handler. */
  refetch: () => void;
}

/**
 * Server-paged roster list with "Show more" appending the next page (mirrors useFolders /
 * useTeacherAssignments). Search/sort changes reset to page one via a fresh `buildQuery` identity.
 * A page-one failure surfaces `hasError`; a "Show more" failure keeps the loaded rows and toasts.
 * Subscribes to the state tree so reused-flow mutations (remove student, resend invite) that
 * invalidate the interest refresh the list — compiled components skip the old top-down re-read.
 */
export const useRosterPagination = <Row extends {getId: () => string}>({
  buildQuery,
  label
}: Args): Result<Row> => {
  const [rows, setRows] = useState<Row[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  // Latest-wins guard: a stale response must not overwrite newer results (see useFolders).
  const genRef = useRef(0);

  const fetchPage = useCallback(
    async (pageNum: number) => {
      const generation = (genRef.current += 1);
      try {
        if (pageNum === 1) {
          setIsLoading(true);
          setHasError(false);
        } else {
          setIsLoadingMore(true);
        }
        const query = buildQuery(pageNum);
        query.invalidateInterest();
        const result = (await query.getResourcePromise()) as {toArray: () => Row[]};
        if (generation !== genRef.current) {
          return;
        }
        const meta = query.getResourceMetadata();
        setTotalPages(meta.getIn(['page', 'total_pages'], 0));
        const pageRows = result.toArray();
        setRows((prev) => {
          if (pageNum === 1) {
            return pageRows;
          }
          // Dedupe appends: offset pages can shift if a row was added/removed mid-scroll.
          const seen = new Set(prev.map((row) => row.getId()));
          return [...prev, ...pageRows.filter((row) => !seen.has(row.getId()))];
        });
      } catch (e) {
        if (generation !== genRef.current) {
          return;
        }
        if (pageNum === 1) {
          setHasError(true);
        } else {
          // Keep the loaded rows and roll the cursor back so a retry re-fetches this page rather than
          // skipping it (guarded above, so a newer in-flight fetch's page isn't clobbered).
          setPage(pageNum - 1);
          addToast({
            color: 'negative',
            title: `Couldn't load more ${label}`,
            message: normalizeError(e).message
          });
        }
      } finally {
        // A stale finally must not kill the newer request's spinner.
        if (generation === genRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [buildQuery, label]
  );

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- effect intentionally kicks off a data fetch; the synchronous setState publishes its loading/reset state
    setPage(1);
    fetchPage(1);
  }, [fetchPage]);

  useEffect(() => {
    const onChange = () => {
      if (buildQuery(1).isResourceStale()) {
        setPage(1);
        fetchPage(1);
      }
    };
    getActiveState().on('change', onChange);
    return () => {
      getActiveState().off('change', onChange);
    };
  }, [buildQuery, fetchPage]);

  const loadMore = useCallback(() => {
    // Ignore clicks while a page-one refetch (search/sort) or another "Show more" is already in flight
    if (isLoading || isLoadingMore) {
      return;
    }
    const next = page + 1;
    setPage(next);
    fetchPage(next);
  }, [isLoading, isLoadingMore, page, fetchPage]);

  const refetch = useCallback(() => {
    setPage(1);
    fetchPage(1);
  }, [fetchPage]);

  return {
    rows,
    isLoading,
    isLoadingMore,
    hasNextPage: page < totalPages,
    hasError,
    loadMore,
    refetch
  };
};
